// Regular-games Agent tab — mirrors the playEngine agent UX (prompt, credits,
// summary, undo, version history, admin console) but runs against agentType "games".

import React, { useState, useEffect, useRef } from "react";
import { Typography } from "@material-ui/core";
import {
  runAgentOverSocket,
  fetchRunStatus,
  pollRunUntilSettled,
  acknowledgeRun,
  listAgentCheckpoints,
  restoreAgentCheckpoint,
  fetchBaseAgentMessages,
  upsertBaseAgentMessage,
  deleteBaseAgentMessage,
} from "../modules/agentSocket";
import {
  AgentPromptCard,
  AgentResponseCard,
  AgentVersionHistory,
  AgentAdminConsole,
  AgentRestoreConfirmDialog,
  AgentContinueDialog,
  AgentRunningBanner,
  AgentPinnedMessages,
} from "../modules/agentTabShared";
import { hasAgentCreditsRemaining, notifyAgentCreditsExhausted, handleAgentCreditsExhaustedError } from "../modules/agentPlanReset";

const AGENT_TYPE = "games";

const consumedAgentRunByVersion = new Map();
const pendingAgentResultsByVersion = new Map();

function GamesAgent(props) {
  const [prompt, setPrompt] = useState("");
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [consoleOpen, setConsoleOpen] = useState(false);
  const [liveLog, setLiveLog] = useState([]);
  const [remoteRunning, setRemoteRunning] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [checkpoints, setCheckpoints] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const [confirmCheckpoint, setConfirmCheckpoint] = useState(null);
  const [continueInfo, setContinueInfo] = useState(null);
  const [pinnedMessages, setPinnedMessages] = useState([]);
  const activeRunVersionIdRef = useRef(null);
  const selectedVersionIdRef = useRef(null);

  const versionId = props.selectedVersion && props.selectedVersion._id
    ? String(props.selectedVersion._id)
    : null;
  const baseId = props.baseId ? String(props.baseId) : null;

  useEffect(() => {
    selectedVersionIdRef.current = versionId || null;
  }, [versionId]);

  // Agent memory, checkpoints, and session are scoped to baseId — only reset UI when
  // the base changes, not when the user switches between sibling versions.
  useEffect(() => {
    setPrompt("");
    setResponse(null);
    setLoading(false);
    setConsoleOpen(false);
    setLiveLog([]);
    setRemoteRunning(false);
    setCanUndo(false);
    setUndoing(false);
    setCheckpoints([]);
    setHistoryOpen(false);
    setRestoringId(null);
    setConfirmCheckpoint(null);
    setContinueInfo(null);
    setPinnedMessages([]);
    activeRunVersionIdRef.current = null;
  }, [baseId]);

  useEffect(() => {
    if (!baseId) {
      setPinnedMessages([]);
      return undefined;
    }

    let cancelled = false;
    fetchBaseAgentMessages(baseId)
      .then((data) => {
        if (cancelled) return;
        const msgs = (data.messages || []).map((m) => ({
          ...m,
          _localKey: m._id,
        }));
        setPinnedMessages(msgs);
      })
      .catch((err) => {
        if (!cancelled && props.createSnackbar) {
          props.createSnackbar(err.message || "Could not load pinned messages", "error");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [baseId, props.createSnackbar]);

  function handlePinnedDraft(localKey, patch) {
    if (patch && patch.addNew) {
      const key = `draft-${Date.now()}`;
      setPinnedMessages((prev) => [...prev, { _localKey: key, message: "", isAdminMessage: false }]);
      return;
    }
    if (patch && patch.removeLocalKey) {
      setPinnedMessages((prev) => prev.filter((m) => m._localKey !== patch.removeLocalKey));
      return;
    }
    setPinnedMessages((prev) => prev.map((m) => (
      m._localKey === localKey ? { ...m, ...patch } : m
    )));
  }

  async function handlePinnedUpsert(payload) {
    if (!baseId) return;
    const data = await upsertBaseAgentMessage(baseId, {
      messageId: payload.messageId,
      message: payload.message,
      isAdminMessage: payload.isAdminMessage,
    });
    const saved = data.message;
    if (!saved) return;
    setPinnedMessages((prev) => prev.map((m) => (
      m._localKey === payload._localKey ? { ...saved, _localKey: saved._id } : m
    )));
  }

  async function handlePinnedDelete(messageId) {
    if (!baseId) return;
    await deleteBaseAgentMessage(baseId, messageId);
    setPinnedMessages((prev) => prev.filter((m) => m._id !== messageId));
  }

  function isViewingVersion(targetVersionId) {
    if (!targetVersionId) return false;
    return String(targetVersionId) === String(selectedVersionIdRef.current);
  }

  function applyVersionEntry(entry) {
    if (!entry || !entry.versionId || !props.patchVersionFields) return false;
    return props.patchVersionFields(
      String(entry.versionId),
      {
        gameConfig: entry.gameConfig,
        createdLanguages: entry.createdLanguages,
        title: entry.title,
      },
      { persist: false }
    );
  }

  function applyVersionEntries(entries) {
    if (!Array.isArray(entries)) return;
    let refreshedCurrent = false;
    for (const entry of entries) {
      if (!entry || !entry.versionId) continue;
      applyVersionEntry(entry);
      if (isViewingVersion(entry.versionId)) refreshedCurrent = true;
    }
    if (refreshedCurrent && props.doRefresh) {
      props.doRefresh((prev) => prev + 1);
    }
  }

  function applyUpdatedVersions(updatedVersions) {
    applyVersionEntries(updatedVersions);
  }

  function refreshVersionList() {
    if (props.fetchVersions && baseId) {
      void props.fetchVersions(String(baseId), localStorage.getItem("user.companyID"));
    }
  }

  function entryChangesTitle(entry) {
    if (!entry || entry.title == null) return false;
    const existing = (props.versions || []).find((v) => String(v._id) === String(entry.versionId));
    return Boolean(existing && existing.title !== entry.title);
  }

  // Versions created or renamed by the run are not patchable (created versions
  // are not in versions[] yet; rename and folder labels live in the hierarchy
  // tree) — reload the list (which also re-fetches the tree) instead.
  function applyVersionListChanges(result) {
    const created = Array.isArray(result.createdVersions) ? result.createdVersions : [];
    const renamed = Array.isArray(result.renamedVersions) ? result.renamedVersions : [];
    if (created.length === 0 && renamed.length === 0 && !result.hierarchyChanged) return;
    refreshVersionList();
    if (!props.createSnackbar) return;
    if (created.length > 0) {
      const titles = created.map((v) => `"${v.title || v.versionId}"`).join(", ");
      props.createSnackbar(
        created.length === 1 ? `Agent created version ${titles}` : `Agent created versions ${titles}`,
        "success"
      );
    }
    if (renamed.length > 0) {
      const labels = renamed
        .map((v) => `"${v.previousTitle || v.versionId}" → "${v.title}"`)
        .join(", ");
      props.createSnackbar(`Agent renamed ${labels}`, "success");
    }
  }

  function applyRestoredVersions(restoredVersions) {
    // A restore can revert renames; the tree label comes from the hierarchy
    // doc (synced server-side), so reload the list to refresh it.
    const titleReverted = Array.isArray(restoredVersions) && restoredVersions.some(entryChangesTitle);
    applyVersionEntries(restoredVersions);
    if (titleReverted) refreshVersionList();
  }

  function findVersionTitle(targetVersionId) {
    if (!targetVersionId || !props.versions) return null;
    const match = props.versions.find((v) => String(v._id) === String(targetVersionId));
    return match && match.title;
  }

  function markRunConsumed(runVersionId, agentRunId) {
    if (runVersionId && agentRunId) {
      consumedAgentRunByVersion.set(runVersionId, agentRunId);
    }
  }

  function isRunAlreadyConsumed(runVersionId, agentRunId) {
    return Boolean(agentRunId && consumedAgentRunByVersion.get(runVersionId) === agentRunId);
  }

  async function consumeRunOnServer(runVersionId) {
    if (!runVersionId) return;
    await acknowledgeRun(runVersionId);
  }

  function applySuccessResult(result, { away = false, runVersionId: runVersionIdOpt } = {}) {
    const runVersionId = runVersionIdOpt || activeRunVersionIdRef.current || versionId;
    const isViewingRunVersion = isViewingVersion(runVersionId);
    const agentRunId = result.agentRunId;

    // A pending result is re-applied when the user switches to the run's version;
    // only handle created versions (refetch + snackbar) on first arrival.
    const alreadyConsumed = isRunAlreadyConsumed(runVersionId, agentRunId);
    markRunConsumed(runVersionId, agentRunId);
    void consumeRunOnServer(runVersionId);
    if (!alreadyConsumed) applyVersionListChanges(result);

    if (isViewingRunVersion) {
      setResponse({
        summary: result.summary,
        changes: result.changes || [],
        creditCost: result.creditCost != null ? result.creditCost : null,
        balanceAfter: result.balanceAfter != null ? result.balanceAfter : null,
        debugLog: result.debugLog || [],
        tokenUsage: result.tokenUsage || null,
        providerName: result.providerName || null,
        model: result.model || null,
        usdCost: result.usdCost != null ? result.usdCost : null,
        pricingExplanation: result.pricingExplanation || null,
      });
      applyUpdatedVersions(result.updatedVersions);
      if (props.refreshCompany) void props.refreshCompany();
      setCanUndo(Boolean(agentRunId));
      void fetchCheckpoints(runVersionId);
      if (result.paused) {
        // Segment paused at its turn limit — ask the user whether to continue.
        setContinueInfo({
          summary: result.summary,
          cumulativeTurns: result.cumulativeTurns,
          canContinue: result.canContinue !== false,
        });
      }
      if (away && props.createSnackbar) {
        props.createSnackbar(
          result.paused
            ? "Agent paused at its turn limit while you were away"
            : "Agent run completed while you were away",
          result.paused ? "info" : "success"
        );
      }
      return;
    }

    pendingAgentResultsByVersion.set(runVersionId, {
      type: "success",
      result,
      away: true,
    });
    const title = findVersionTitle(runVersionId);
    if (props.createSnackbar) {
      props.createSnackbar(
        title
          ? `Agent run completed on "${title}"`
          : "Agent run completed on another version",
        "success"
      );
    }
  }

  function applyFailureStatus(status, { away = false, runVersionId: runVersionIdOpt } = {}) {
    const runVersionId = runVersionIdOpt || activeRunVersionIdRef.current || versionId;
    const isViewingRunVersion = isViewingVersion(runVersionId);
    const agentRunId = status.agentRunId;

    const alreadyConsumed = isRunAlreadyConsumed(runVersionId, agentRunId);
    markRunConsumed(runVersionId, agentRunId);
    void consumeRunOnServer(runVersionId);

    // Rollback restores edits but does not delete versions the failed run
    // created — refresh the list so any leftover new versions show up.
    const leftoverCreated = Array.isArray(status.createdVersions) ? status.createdVersions : [];
    if (!alreadyConsumed && (leftoverCreated.length > 0 || status.hierarchyChanged)) {
      refreshVersionList();
    }

    if (isViewingRunVersion) {
      applyRestoredVersions(status.restoredVersions);
      setResponse({
        error: status.error || "Agent run failed",
        restored: Boolean(status.restored),
        creditCost: status.creditCost != null ? status.creditCost : null,
        balanceAfter: status.balanceAfter != null ? status.balanceAfter : null,
        pricingExplanation: status.pricingExplanation || null,
      });
      if (props.refreshCompany) void props.refreshCompany();
      if (away && props.createSnackbar) {
        props.createSnackbar("Agent run failed while you were away", "warning");
      }
      return;
    }

    pendingAgentResultsByVersion.set(runVersionId, {
      type: "failed",
      status,
      away: true,
    });
    const title = findVersionTitle(runVersionId);
    if (props.createSnackbar) {
      props.createSnackbar(
        title
          ? `Agent run failed on "${title}"`
          : "Agent run failed on another version",
        "warning"
      );
    }
  }

  function applyPendingResultForVersion(targetVersionId) {
    const pending = pendingAgentResultsByVersion.get(targetVersionId);
    if (!pending) return;
    pendingAgentResultsByVersion.delete(targetVersionId);
    if (pending.type === "success") {
      applySuccessResult(pending.result, { away: pending.away, runVersionId: targetVersionId });
    } else if (pending.type === "failed") {
      applyFailureStatus(pending.status, { away: pending.away, runVersionId: targetVersionId });
    }
  }

  useEffect(() => {
    if (!versionId) return undefined;

    let cancelled = false;
    let pollTimer = null;
    const statusVersionId = activeRunVersionIdRef.current || versionId;

    async function handleSettledStatus(status, { away = true } = {}) {
      const settledVersionId = status.versionId || statusVersionId;
      const agentRunId = status.agentRunId || (status.result && status.result.agentRunId);
      if (agentRunId && isRunAlreadyConsumed(settledVersionId, agentRunId)) {
        await consumeRunOnServer(settledVersionId);
        applyPendingResultForVersion(settledVersionId);
        return;
      }

      if (status.status === "completed" && status.result) {
        applySuccessResult(status.result, { away, runVersionId: settledVersionId });
      } else if (status.status === "failed") {
        applyFailureStatus(status, { away, runVersionId: settledVersionId });
      } else {
        applyPendingResultForVersion(versionId);
      }
    }

    async function checkRunStatus() {
      try {
        const status = await fetchRunStatus(statusVersionId);
        if (cancelled) return;

        if (status.status === "running") {
          setRemoteRunning(true);
          setLoading(true);
          pollTimer = setInterval(async () => {
            try {
              const next = await fetchRunStatus(statusVersionId);
              if (cancelled) return;
              if (next.status !== "running") {
                clearInterval(pollTimer);
                pollTimer = null;
                setRemoteRunning(false);
                setLoading(false);
                await handleSettledStatus(next);
              }
            } catch (_err) {
              // Keep polling on transient network errors.
            }
          }, 3000);
        } else if (status.status === "completed" || status.status === "failed") {
          await handleSettledStatus(status);
        } else {
          applyPendingResultForVersion(versionId);
        }
      } catch (_err) {
        // Best-effort on load.
      }
    }

    checkRunStatus();

    return () => {
      cancelled = true;
      if (pollTimer) clearInterval(pollTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionId]);

  async function fetchCheckpoints(targetVersionId = versionId) {
    if (!targetVersionId) return;
    try {
      const data = await listAgentCheckpoints(targetVersionId, { agentType: AGENT_TYPE });
      if (data.ok && Array.isArray(data.checkpoints) && isViewingVersion(targetVersionId)) {
        setCheckpoints(data.checkpoints);
      }
    } catch (_err) {
      // Best-effort — drawer stays empty.
    }
  }

  useEffect(() => {
    if (!versionId) return;
    // Checkpoints are base-scoped; any sibling versionId resolves the same list.
    fetchCheckpoints(versionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseId]);

  async function handleRestoreCheckpoint(checkpointId) {
    if (!versionId || !checkpointId || restoringId || loading || remoteRunning) return;
    setRestoringId(checkpointId);
    try {
      const data = await restoreAgentCheckpoint(versionId, {
        agentType: AGENT_TYPE,
        checkpointId,
      });
      if (data.ok && Array.isArray(data.restoredVersions)) {
        applyRestoredVersions(data.restoredVersions, versionId);
        setCanUndo(false);
        if (props.createSnackbar) props.createSnackbar("Restored checkpoint", "success");
        await fetchCheckpoints(versionId);
      } else {
        if (props.createSnackbar) props.createSnackbar(data.error || "Could not restore checkpoint", "warning");
        await fetchCheckpoints(versionId);
      }
    } catch (err) {
      if (props.createSnackbar) props.createSnackbar(err.message || "Restore failed", "error");
    } finally {
      setRestoringId(null);
    }
  }

  async function confirmRestore() {
    const target = confirmCheckpoint;
    setConfirmCheckpoint(null);
    if (target && target.checkpointId) {
      await handleRestoreCheckpoint(target.checkpointId);
    }
  }

  async function restoreCheckpointFromServer(targetVersionId = versionId) {
    if (!targetVersionId) return false;
    try {
      const data = await restoreAgentCheckpoint(targetVersionId, { agentType: AGENT_TYPE });
      if (data.ok && Array.isArray(data.restoredVersions) && data.restoredVersions.length > 0) {
        applyRestoredVersions(data.restoredVersions, targetVersionId);
        return true;
      }
    } catch (_err) {
      // Caller surfaces the original failure.
    }
    return false;
  }

  async function handleRun(opts = {}) {
    // Called both from the prompt card (click event as opts) and the continue
    // dialog ({ continueRun: true }).
    const isContinue = opts && opts.continueRun === true;
    if (!isContinue && !prompt.trim()) return;
    if (!versionId || remoteRunning) return;
    if (!hasAgentCreditsRemaining(props.userCompany)) {
      notifyAgentCreditsExhausted(props.createSnackbar);
      return;
    }
    const runVersionId = versionId;
    setLoading(true);
    setResponse(null);
    setLiveLog([]);
    setCanUndo(false);
    setRemoteRunning(false);
    setContinueInfo(null);
    activeRunVersionIdRef.current = runVersionId;

    try {
      const result = await runAgentOverSocket({
        agentType: AGENT_TYPE,
        versionId: runVersionId,
        prompt: isContinue ? "" : prompt.trim(),
        continueRun: isContinue,
        onEvent: props.isAdmin
          ? (event) => {
            if (isViewingVersion(runVersionId)) {
              setLiveLog((prev) => [...prev, event]);
            }
          }
          : undefined,
      });

      applySuccessResult(result, { runVersionId });
    } catch (err) {
      if (handleAgentCreditsExhaustedError(err, props.createSnackbar)) return;
      if (err.code === "ALREADY_RUNNING") {
        if (isViewingVersion(runVersionId)) {
          setRemoteRunning(true);
          setResponse({ error: err.message || "An agent run is already in progress for this version" });
        }
        try {
          const status = await pollRunUntilSettled(runVersionId);
          if (status.status === "completed" && status.result) {
            applySuccessResult(status.result, { runVersionId });
          } else if (status.status === "failed") {
            applyFailureStatus(status, { runVersionId });
          }
        } catch (pollErr) {
          if (isViewingVersion(runVersionId)) {
            setResponse({ error: pollErr.message || err.message });
          }
        }
        return;
      }

      if (err.isDisconnect) {
        if (isViewingVersion(runVersionId)) {
          setResponse({ error: "Connection lost — waiting for agent run to finish…" });
        }
        try {
          const status = await pollRunUntilSettled(runVersionId);
          if (status.status === "completed" && status.result) {
            applySuccessResult(status.result, { runVersionId });
          } else if (status.status === "failed") {
            applyFailureStatus(status, { runVersionId });
          } else if (isViewingVersion(runVersionId)) {
            setResponse({ error: "Connection lost and run status could not be determined" });
          }
        } catch (pollErr) {
          if (isViewingVersion(runVersionId)) {
            setResponse({ error: pollErr.message || "Connection lost while agent was running" });
          }
        }
        return;
      }

      if ((Array.isArray(err.createdVersions) && err.createdVersions.length > 0) || err.hierarchyChanged) {
        // Versions created and folder changes made before the failure are not
        // rolled back — refresh the list.
        refreshVersionList();
      }

      let restored = false;
      if (Array.isArray(err.restoredVersions) && err.restoredVersions.length > 0) {
        applyRestoredVersions(err.restoredVersions);
        restored = true;
      } else if (err.restored) {
        restored = true;
      } else {
        restored = await restoreCheckpointFromServer(runVersionId);
      }

      if (isViewingVersion(runVersionId)) {
        setResponse({
          error: err.message || "Agent run failed",
          restored,
          creditCost: err.creditCost != null ? err.creditCost : null,
          balanceAfter: err.balanceAfter != null ? err.balanceAfter : null,
          pricingExplanation: err.pricingExplanation || null,
        });
        if (props.refreshCompany) void props.refreshCompany();
      }
    } finally {
      if (activeRunVersionIdRef.current === runVersionId) {
        activeRunVersionIdRef.current = null;
      }
      setLoading(false);
      setRemoteRunning(false);
    }
  }

  async function handleUndo() {
    if (!versionId || undoing) return;
    setUndoing(true);
    try {
      const data = await restoreAgentCheckpoint(versionId, { agentType: AGENT_TYPE });
      if (data.ok && Array.isArray(data.restoredVersions) && data.restoredVersions.length > 0) {
        applyRestoredVersions(data.restoredVersions, versionId);
        setCanUndo(false);
        void fetchCheckpoints(versionId);
        if (props.createSnackbar) props.createSnackbar("Reverted to the previous version", "success");
      } else {
        setCanUndo(false);
        void fetchCheckpoints(versionId);
        if (props.createSnackbar) props.createSnackbar(data.error || "Nothing to undo", "warning");
      }
    } catch (err) {
      if (props.createSnackbar) props.createSnackbar(err.message || "Undo failed", "error");
    } finally {
      setUndoing(false);
    }
  }

  function handleContinueRun() {
    setContinueInfo(null);
    void handleRun({ continueRun: true });
  }

  function handleStopKeep() {
    // Work done so far is already applied and undoable — nothing else to do.
    setContinueInfo(null);
  }

  async function handleStopUndo() {
    setContinueInfo(null);
    await handleUndo();
  }

  const debugLog = (liveLog && liveLog.length > 0) ? liveLog : (response && response.debugLog);
  const tokenUsage = response && response.tokenUsage;
  const providerName = response && response.providerName;
  const agentModel = response && response.model;
  const usdCost = response && response.usdCost;
  const pricingExplanation = response && response.pricingExplanation;

  const isVersionLocked = props.isVersionLocked === true;

  return (
    <div style={{ padding: "12px", display: "flex", flexDirection: "column", gap: "12px", height: "100%", boxSizing: "border-box" }}>
      {!versionId && (
        <Typography variant="body2" style={{ color: "#f44336", fontSize: "12px" }}>
          No version selected.
        </Typography>
      )}

      {isVersionLocked && (
        <Typography variant="body2" style={{ color: "#ffb74d", fontSize: "12px" }}>
          This version is locked. The agent cannot edit locked versions.
        </Typography>
      )}

      <AgentPinnedMessages
        baseId={baseId}
        isAdmin={props.isAdmin === true}
        messages={pinnedMessages}
        onUpsert={handlePinnedUpsert}
        onDelete={handlePinnedDelete}
        onAddDraft={handlePinnedDraft}
        createSnackbar={props.createSnackbar}
      />

      <AgentRunningBanner loading={loading} remoteRunning={remoteRunning} />

      <AgentPromptCard
        prompt={prompt}
        onPromptChange={(e) => setPrompt(e.target.value)}
        onRun={handleRun}
        loading={loading}
        remoteRunning={remoteRunning}
        versionId={versionId}
        userCompany={props.userCompany}
        placeholder="Describe what you want the agent to do with this game version..."
        runLabel="Run Agent"
        runDisabled={isVersionLocked}
      />

      <AgentResponseCard
        response={response}
        canUndo={canUndo}
        undoing={undoing}
        onUndo={handleUndo}
        undoDisabled={isVersionLocked}
      />

      <div style={{ marginTop: "auto", display: "flex", flexDirection: "column" }}>
        <AgentVersionHistory
          versionId={versionId}
          checkpoints={checkpoints}
          historyOpen={historyOpen}
          onToggleHistory={() => setHistoryOpen((v) => !v)}
          restoringId={restoringId}
          loading={loading}
          remoteRunning={remoteRunning}
          onRequestRestore={setConfirmCheckpoint}
          restoreDisabled={isVersionLocked}
        />

        <AgentAdminConsole
          isAdmin={props.isAdmin}
          consoleOpen={consoleOpen}
          onToggleConsole={() => setConsoleOpen((v) => !v)}
          debugLog={debugLog}
          loading={loading}
          providerName={providerName}
          agentModel={agentModel}
          tokenUsage={tokenUsage}
          usdCost={usdCost}
          pricingExplanation={pricingExplanation}
        />
      </div>

      <AgentRestoreConfirmDialog
        open={Boolean(confirmCheckpoint)}
        onCancel={() => setConfirmCheckpoint(null)}
        onConfirm={confirmRestore}
      />

      <AgentContinueDialog
        open={Boolean(continueInfo)}
        info={continueInfo}
        onContinue={handleContinueRun}
        onKeep={handleStopKeep}
        onUndo={handleStopUndo}
        undoDisabled={isVersionLocked || undoing}
      />
    </div>
  );
}

export default GamesAgent;
